import React, { useState, useEffect } from 'react';
import {
  Container,
  Card,
  Button,
  Form,
  Badge,
  Spinner,
  Modal,
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../services/api';

const OnlineService = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('new'); // 'new' or 'history'
  const [loading, setLoading] = useState(false);
  const [requests, setRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showModal, setShowModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const openAttachment = async (event, url) => {
    if (!url?.includes('/api/files/private/')) return;
    event.preventDefault();
    try {
      const response = await api.get(url, { responseType: 'blob' });
      const objectUrl = URL.createObjectURL(response.data);
      window.open(objectUrl, '_blank', 'noopener,noreferrer');
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
    } catch {
      toast.error('Unable to open attachment');
    }
  };

  const [formData, setFormData] = useState({
    subject: '',
    category: 'GENERAL',
    message: '',
    priority: 'MEDIUM',
  });

  const categories = [
    'GENERAL',
    'TECHNICAL',
    'ACCOUNT',
    'TRADING',
    'KYC',
    'WITHDRAWAL',
    'DEPOSIT',
    'OTHER',
  ];

  const priorities = [
    { value: 'LOW', color: 'success' },
    { value: 'MEDIUM', color: 'warning' },
    { value: 'HIGH', color: 'danger' },
    { value: 'URGENT', color: 'dark' },
  ];

  useEffect(() => {
    fetchRequests();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchRequestDetail = async (id) => {
    setLoadingDetail(true);
    setSelectedRequest(null);
    setShowModal(true);
    try {
      const response = await api.get(`/api/service-requests/${id}`);
      if (response.data?.success) {
        setSelectedRequest(response.data.data.request);
      }
    } catch (error) {
      toast.error('Failed to load request details', {
        position: 'bottom-left',
        autoClose: 3000,
      });
      setShowModal(false);
    } finally {
      setLoadingDetail(false);
    }
  };

  const fetchRequests = async () => {
    setLoadingRequests(true);
    try {
      const response = await api.get('/api/service-requests');
      if (response.data?.success) {
        setRequests(response.data.data.requests || []);
      }
    } catch (error) {
    } finally {
      setLoadingRequests(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.subject.trim() || !formData.message.trim()) {
      toast.error('Please fill in all required fields', {
        position: 'bottom-left',
        autoClose: 3000,
      });
      return;
    }

    setLoading(true);
    try {
      await api.post('/api/service-requests', formData);

      toast.success('Message sent successfully!', {
        position: 'bottom-left',
        autoClose: 3000,
      });

      // Reset form
      setFormData({
        subject: '',
        category: 'GENERAL',
        message: '',
        priority: 'MEDIUM',
      });

      fetchRequests();
      setActiveTab('history');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to send message', {
        position: 'bottom-left',
        autoClose: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container
      fluid
      style={{
        maxWidth: isMobile ? '100%' : '85%',
        padding: 'clamp(1rem, 3vw, 1.5rem)',
        paddingTop: '20px',
        paddingBottom: '100px',
        backgroundColor: '#FFFFFF',
        minHeight: '100vh',
      }}
    >
      {/* Header */}
      <div className='d-flex justify-content-between align-items-center mb-4'>
        <Button
          variant='link'
          onClick={() => navigate(-1)}
          className='p-0'
          style={{
            minWidth: '40px',
            textDecoration: 'none',
            color: '#333',
          }}
        >
          <i
            className='bi bi-arrow-left'
            style={{ fontSize: '1.4rem', fontWeight: 'bold' }}
          ></i>
        </Button>
        <h5
          className='mb-0 fw-bold'
          style={{
            fontSize: '1.25rem',
            letterSpacing: '0.3px',
            color: '#333',
          }}
        >
          Online Service
        </h5>
        <div style={{ width: '40px' }}></div>
      </div>

      {/* Tabs */}
      <div className='d-flex gap-3 mb-4'>
        <Button
          className='flex-fill'
          onClick={() => setActiveTab('new')}
          style={{
            background: activeTab === 'new' ? '#667eea' : '#f8f9fa',
            color: activeTab === 'new' ? '#FFFFFF' : '#667eea',
            border: activeTab === 'new' ? 'none' : '2px solid #667eea',
            borderRadius: '12px',
            padding: '12px 16px',
            fontWeight: '600',
            fontSize: '0.95rem',
            transition: 'all 0.3s ease',
            boxShadow:
              activeTab === 'new'
                ? '0 4px 12px rgba(102, 126, 234, 0.3)'
                : 'none',
          }}
        >
          <i className='bi bi-send me-2'></i>
          Send Message
        </Button>
        <Button
          className='flex-fill'
          onClick={() => setActiveTab('history')}
          style={{
            background: activeTab === 'history' ? '#667eea' : '#f8f9fa',
            color: activeTab === 'history' ? '#FFFFFF' : '#667eea',
            border: activeTab === 'history' ? 'none' : '2px solid #667eea',
            borderRadius: '12px',
            padding: '12px 16px',
            fontWeight: '600',
            fontSize: '0.95rem',
            transition: 'all 0.3s ease',
            boxShadow:
              activeTab === 'history'
                ? '0 4px 12px rgba(102, 126, 234, 0.3)'
                : 'none',
          }}
        >
          <i className='bi bi-inbox me-2'></i>
          My Messages
        </Button>
      </div>

      {/* Main Content */}
      <div>
        {/* New Message Tab */}
        {activeTab === 'new' && (
          <Card className='border-0 shadow-sm'>
            <Card.Body className='p-4'>
              <Form onSubmit={handleSubmit}>
                <Form.Group className='mb-3'>
                  <Form.Label className='fw-semibold'>Subject</Form.Label>
                  <Form.Control
                    type='text'
                    name='subject'
                    placeholder='Enter subject'
                    value={formData.subject}
                    onChange={handleInputChange}
                    style={{
                      borderRadius: '10px',
                      padding: '12px',
                      border: '1px solid #e0e0e0',
                    }}
                    required
                  />
                </Form.Group>

                <Form.Group className='mb-3'>
                  <Form.Label className='fw-semibold'>Category</Form.Label>
                  <Form.Select
                    name='category'
                    value={formData.category}
                    onChange={handleInputChange}
                    style={{
                      borderRadius: '10px',
                      padding: '12px',
                      border: '1px solid #e0e0e0',
                    }}
                    required
                  >
                    {categories.map((cat) => (
                      <option key={cat} value={cat}>
                        {cat}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <Form.Group className='mb-3'>
                  <Form.Label className='fw-semibold'>Priority</Form.Label>
                  <Form.Select
                    name='priority'
                    value={formData.priority}
                    onChange={handleInputChange}
                    style={{
                      borderRadius: '10px',
                      padding: '12px',
                      border: '1px solid #e0e0e0',
                    }}
                    required
                  >
                    {priorities.map((pri) => (
                      <option key={pri.value} value={pri.value}>
                        {pri.value}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <Form.Group className='mb-4'>
                  <Form.Label className='fw-semibold'>Message</Form.Label>
                  <Form.Control
                    as='textarea'
                    rows={6}
                    name='message'
                    placeholder='Type your message here...'
                    value={formData.message}
                    onChange={handleInputChange}
                    style={{
                      borderRadius: '10px',
                      padding: '12px',
                      border: '1px solid #e0e0e0',
                    }}
                    required
                  />
                </Form.Group>

                <div className='d-grid'>
                  <Button
                    type='submit'
                    size='lg'
                    disabled={loading}
                    style={{
                      background:
                        'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      border: 'none',
                      borderRadius: '10px',
                      padding: '14px',
                      fontWeight: 'bold',
                    }}
                  >
                    {loading ? (
                      <>
                        <Spinner
                          as='span'
                          animation='border'
                          size='sm'
                          className='me-2'
                        />
                        Sending...
                      </>
                    ) : (
                      <>
                        <i className='bi bi-send me-2'></i>
                        Send Message
                      </>
                    )}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        )}

        {/* Message History Tab */}
        {activeTab === 'history' && (
          <div>
            {loadingRequests ? (
              <div className='text-center py-5'>
                <Spinner animation='border' style={{ color: '#667eea' }} />
              </div>
            ) : requests.length === 0 ? (
              <Card className='border-0 shadow-sm text-center py-5'>
                <Card.Body>
                  <i
                    className='bi bi-inbox'
                    style={{ fontSize: '3rem', color: '#667eea' }}
                  ></i>
                  <p className='mt-3 mb-0 text-muted fw-semibold'>
                    No messages yet
                  </p>
                  <small className='text-muted'>
                    Send your first message to get started
                  </small>
                </Card.Body>
              </Card>
            ) : (
              requests.map((req) => {
                const statusColor =
                  req.status === 'OPEN'
                    ? '#2563eb'
                    : req.status === 'RESOLVED'
                      ? '#16a34a'
                      : req.status === 'CLOSED'
                        ? '#6b7280'
                        : '#667eea';
                const priorityBg =
                  priorities.find((p) => p.value === req.priority)?.color ||
                  'secondary';
                return (
                  <Card key={req.id} className='mb-3 border-0 shadow-sm'>
                    <Card.Body className='p-3'>
                      {/* Top row: Category + Priority + Status + Date */}
                      <div className='d-flex flex-wrap align-items-center gap-2 mb-2'>
                        <Badge
                          style={{
                            background:
                              'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            fontSize: '0.7rem',
                          }}
                        >
                          {req.category}
                        </Badge>
                        <Badge bg={priorityBg} style={{ fontSize: '0.7rem' }}>
                          {req.priority}
                        </Badge>
                        <span
                          style={{
                            fontSize: '0.72rem',
                            fontWeight: '700',
                            color: '#fff',
                            background: statusColor,
                            borderRadius: '20px',
                            padding: '2px 10px',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {req.status}
                        </span>
                        <small
                          className='text-muted ms-auto'
                          style={{ fontSize: '0.75rem' }}
                        >
                          <i className='bi bi-calendar3 me-1'></i>
                          {formatDate(req.createdAt)}
                        </small>
                      </div>

                      {/* Message block + View button side by side */}
                      <div className='d-flex align-items-center gap-2'>
                        <div
                          className='p-2 rounded flex-grow-1'
                          style={{
                            background: '#f8f9fa',
                            borderLeft: '3px solid #667eea',
                          }}
                        >
                          <small className='text-muted fw-semibold'>
                            <i className='bi bi-tag me-1'></i>Subject:
                          </small>{' '}
                          <small className='text-dark'>{req.subject}</small>
                          <br />
                          <small className='text-muted fw-semibold'>
                            <i className='bi bi-chat-left-text me-1'></i>Your
                            Message:
                          </small>{' '}
                          <small className='text-dark'>{req.message}</small>
                        </div>
                        <Button
                          size='sm'
                          onClick={() => fetchRequestDetail(req.id)}
                          style={{
                            background:
                              'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            border: 'none',
                            borderRadius: '8px',
                            fontSize: '0.78rem',
                            fontWeight: '600',
                            padding: '6px 14px',
                            color: '#fff',
                            whiteSpace: 'nowrap',
                            flexShrink: 0,
                          }}
                        >
                          <i className='bi bi-eye me-1'></i>View
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                );
              })
            )}
          </div>
        )}
      </div>

      {/* Request Detail Modal */}
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        centered
        scrollable
      >
        <Modal.Header
          closeButton
          style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: '#fff',
          }}
        >
          <Modal.Title style={{ fontSize: '1rem', fontWeight: '700' }}>
            <i className='bi bi-ticket-detailed me-2'></i>
            Request Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className='p-3'>
          {loadingDetail ? (
            <div className='text-center py-4'>
              <Spinner animation='border' style={{ color: '#667eea' }} />
            </div>
          ) : selectedRequest ? (
            <div>
              {/* Status + Category + Priority */}
              <div className='d-flex flex-wrap align-items-center gap-2 mb-3'>
                <span
                  style={{
                    fontSize: '0.72rem',
                    fontWeight: '700',
                    color: '#fff',
                    background:
                      selectedRequest.status === 'OPEN'
                        ? '#2563eb'
                        : selectedRequest.status === 'RESOLVED'
                          ? '#16a34a'
                          : '#6b7280',
                    borderRadius: '20px',
                    padding: '2px 10px',
                  }}
                >
                  {selectedRequest.status}
                </span>
                <Badge
                  style={{
                    background:
                      'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    fontSize: '0.7rem',
                  }}
                >
                  {selectedRequest.category}
                </Badge>
                <Badge
                  bg={
                    priorities.find((p) => p.value === selectedRequest.priority)
                      ?.color || 'secondary'
                  }
                  style={{ fontSize: '0.7rem' }}
                >
                  {selectedRequest.priority}
                </Badge>
                <small
                  className='text-muted ms-auto'
                  style={{ fontSize: '0.72rem' }}
                >
                  <i className='bi bi-calendar3 me-1'></i>
                  {formatDate(selectedRequest.createdAt)}
                </small>
              </div>

              {/* Subject & Message */}
              <div
                className='p-3 rounded mb-3'
                style={{
                  background: '#f8f9fa',
                  borderLeft: '3px solid #667eea',
                }}
              >
                <div className='mb-2'>
                  <small className='text-muted fw-semibold'>
                    <i className='bi bi-tag me-1'></i>Subject:
                  </small>{' '}
                  <small className='text-dark'>{selectedRequest.subject}</small>
                </div>
                <div>
                  <small className='text-muted fw-semibold'>
                    <i className='bi bi-chat-left-text me-1'></i>Your Message:
                  </small>{' '}
                  <small className='text-dark'>{selectedRequest.message}</small>
                </div>
              </div>

              {/* Admin Replies */}
              {selectedRequest.messages?.some((m) => m.isAdminReply) && (
                <div>
                  <small
                    className='fw-bold text-muted d-block mb-2'
                    style={{ fontSize: '0.78rem' }}
                  >
                    <i className='bi bi-shield-check me-1'></i>Replies from
                    Support
                  </small>
                  {selectedRequest.messages
                    .filter((m) => m.isAdminReply)
                    .map((msg) => (
                      <div
                        key={msg.id}
                        className='p-2 rounded mb-2'
                        style={{
                          background: '#eef2ff',
                          borderLeft: '3px solid #764ba2',
                          fontSize: '0.82rem',
                        }}
                      >
                        <div className='d-flex justify-content-between align-items-center mb-1'>
                          <span
                            style={{
                              fontWeight: '700',
                              color: '#764ba2',
                              fontSize: '0.75rem',
                            }}
                          >
                            <i className='bi bi-shield-check me-1'></i>Support
                            Agent
                          </span>
                          <small
                            className='text-muted'
                            style={{ fontSize: '0.7rem' }}
                          >
                            {formatDate(msg.createdAt)}
                          </small>
                        </div>
                        <div className='text-dark'>{msg.message}</div>
                        {msg.attachmentUrl && (
                          <a
                            href={msg.attachmentUrl}
                            onClick={(event) => openAttachment(event, msg.attachmentUrl)}
                            target='_blank'
                            rel='noreferrer'
                            className='d-block mt-1'
                            style={{ fontSize: '0.75rem', color: '#667eea' }}
                          >
                            <i className='bi bi-paperclip me-1'></i>Attachment
                          </a>
                        )}
                      </div>
                    ))}
                </div>
              )}
              {!selectedRequest.messages?.some((m) => m.isAdminReply) && (
                <small
                  className='text-muted d-block text-center py-2'
                  style={{ fontSize: '0.78rem' }}
                >
                  <i className='bi bi-hourglass-split me-1'></i>Awaiting reply
                  from support
                </small>
              )}
            </div>
          ) : null}
        </Modal.Body>
        <Modal.Footer className='p-2'>
          <Button
            size='sm'
            onClick={() => setShowModal(false)}
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              border: 'none',
              borderRadius: '8px',
              fontWeight: '600',
            }}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <style>{`
        .tab-button.btn-outline-light:hover {
          background-color: rgba(255, 255, 255, 0.2) !important;
          color: white !important;
          border-color: white !important;
        }

        .tab-button.btn-light:hover {
          background-color: white !important;
          color: #667eea !important;
        }
      `}</style>
    </Container>
  );
};

export default OnlineService;
